
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Dialog from 'primevue/dialog';
import Gleap from 'gleap';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { Logout, Me, UserState } from '@/composables/User';
import { useStore } from '@/store';
import { getErrorMessage, showToastError } from '@/helpers';
import { useOnboardingState, getPersistedIndex } from '@/composables/onboarding-v2/useOnboardingState';
import { useOnboardingProfile } from '@/composables/onboarding-v2/useOnboardingProfile';
import { resolveTargetIndex } from '@/composables/onboarding-v2/useOnboardingValidators';
import DotsProgress from '@/components/onboarding-v2/shared/DotsProgress.vue';
import OnboardingV2Components from '@/components/onboarding-v2';
import AuthCompleteLayout from '@/views/layout/AuthCompleteLayout.vue';
import MySitesMenu from '@/components/header/MySitesMenu.vue';

export default defineComponent({
  name: 'CompleteV2',

  components: {
    Dialog,
    SpmButton,
    DotsProgress,
    AuthCompleteLayout,
    MySitesMenu,
    ...OnboardingV2Components,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();

    const {
      totalSteps,
      currentIndex,
      currentStep,
      isFirst,
      isLast,
      setIndex,
      goNext,
      goPrev,
      clearPersisted,
    } = useOnboardingState();

    const laterModalVisible = ref(false);
    const bootLoading = ref(true);

    const { profile, preload } = useOnboardingProfile();

    // Détermine si l'étape courante doit afficher la variante "Alt" du
    // narratif (gauche). Critères côté étape :
    //   - userInfos : tous les champs profil (sauf vatNumber) déjà remplis
    //                 → on demande de "vérifier" plutôt que "renseigner".
    //   - shopConfirm : il MANQUE name OU currency OU langue par défaut
    //                   → on n'a rien deviné, on demande à compléter.
    // Suffix appliqué aux clés i18n du narratif gauche selon le contexte de
    // l'étape :
    //   - 'Alt' : variante "déjà rempli" (userInfos) ou "rien deviné"
    //             (shopConfirm).
    //   - 'Shopify' : étape `connect` pour une boutique Shopify, où le flow
    //                 d'auto-provisioning ne demande pas de copier-coller.
    const narrativeSuffix = computed<string>(() => {
      const stepId = currentStep.value.id;
      if (stepId === 'connect'
        && UserState.activeShop?.solutionEcommerce === 'shopify') {
        return 'Shopify';
      }
      if (stepId === 'userInfos') {
        const filled = !!(
          profile.firstName
          && profile.lastName
          && profile.societe
          && profile.rue
          && profile.zipCode
          && profile.city
          && profile.country
          && profile.phoneMobile
        );
        return filled ? 'Alt' : '';
      }
      if (stepId === 'shopConfirm') {
        const shop = UserState.activeShop;
        const hasDefaultLang = Array.isArray(shop?.langs)
          && shop!.langs.some((l) => l.default);
        const hasAll = !!(shop?.name && shop?.currency && hasDefaultLang);
        return hasAll ? '' : 'Alt';
      }
      return '';
    });

    const narrativeHeadline = computed(
      () => t(`completeV2.steps.${currentStep.value.id}.narrativeHeadline${narrativeSuffix.value}`),
    );
    const narrativeSubtitle = computed(
      () => t(`completeV2.steps.${currentStep.value.id}.narrativeSubtitle${narrativeSuffix.value}`),
    );

    const userShopsCount = computed(() => UserState.user?.shops?.length ?? 0);

    onMounted(async () => {
      try {
        Gleap.setLanguage(UserState.user?.lang ?? 'en');
        Gleap.initialize(process.env.VUE_APP_GLEAP_API_KEY || '');
        Gleap.setMaxNetworkRequests(50);
        Gleap.showFeedbackButton(false);
      } catch (e) {
        // Gleap init failure shouldn't block onboarding
      }

      try {
        await preload();
        const target = await resolveTargetIndex(getPersistedIndex());
        setIndex(target);
      } finally {
        bootLoading.value = false;
      }
    });

    const openLaterModal = () => {
      laterModalVisible.value = true;
    };

    const openSupport = () => {
      try {
        Gleap.open();
      } catch (e) {
        // noop
      }
    };

    const handleNext = async () => {
      if (isLast.value) {
        clearPersisted();
        store.commit('general/showTheSpinner');
        try {
          await Me(true, UserState.activeShop?.id);
        } finally {
          store.commit('general/hideTheSpinner');
        }
        await router.push({ name: 'home' });
        return;
      }
      goNext();
    };

    const handleBack = () => {
      goPrev();
    };

    const handleLogout = async () => {
      laterModalVisible.value = false;
      store.commit('general/showTheSpinner');
      try {
        clearPersisted();
        await Logout();
      } catch (error) {
        await showToastError('GENERIC_ERROR');
      } finally {
        await router.push({ name: 'login' });
        store.commit('general/hideTheSpinner');
        window.location.reload();
      }
    };

    return {
      t,
      totalSteps,
      currentIndex,
      currentStep,
      isFirst,
      narrativeHeadline,
      narrativeSubtitle,
      bootLoading,
      laterModalVisible,
      userShopsCount,
      openLaterModal,
      openSupport,
      handleNext,
      handleBack,
      handleLogout,
    };
  },
});
